import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Box, Plus, Filter, Trash2, Search, QrCode } from 'lucide-react';
import QRScanner from '@/components/QRScanner';
import ScanButton from '@/components/ScanButton';
import UnknownCodeDialog from '@/components/UnknownCodeDialog';
import { hasContent, getAllSlotsFill } from '@/utils/fillDisplay';
import { resolveScan, ScanResultType, ScanContext } from '@/utils/scanResolver';

const AutomatenPage = () => {
  const { user, API } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [automaten, setAutomaten] = useState([]);
  const [filteredAutomaten, setFilteredAutomaten] = useState([]);
  const [financials, setFinancials] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [defectOnlyFilter, setDefectOnlyFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // NEU: Suchfeld
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showPageScanner, setShowPageScanner] = useState(false); // NEU: Separater Scanner für Seite
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [automatToDelete, setAutomatToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [showUnknownCodeDialog, setShowUnknownCodeDialog] = useState(false);
  const [scannedUnknownCode, setScannedUnknownCode] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    qr_value: '',
    machine_type: 'Standard',
    coin_type: '0,50',
    status: 'Lager LEER',
    is_defect: false,        // NEU: Defekt beim Erstellen
    defect_note: '',         // NEU: Defektbeschreibung
  });

  useEffect(() => {
    fetchAutomaten();
  }, [defectOnlyFilter]); // Re-fetch when defect filter changes

  useEffect(() => {
    filterAutomaten();
  }, [statusFilter, automaten, searchQuery]);

  // NEU: Numerische Sortierung für Automatennummern
  const sortedAutomaten = useMemo(() => {
    return [...automaten].sort((a, b) => {
      // Extrahiere nur Ziffern aus dem Code
      const numA = parseInt((a.code || '').replace(/\D/g, ''), 10) || Number.MAX_SAFE_INTEGER;
      const numB = parseInt((b.code || '').replace(/\D/g, ''), 10) || Number.MAX_SAFE_INTEGER;
      return numA - numB;
    });
  }, [automaten]);

  useEffect(() => {
    // Check if we should open create dialog with scanned code
    if (location.state?.openCreate && location.state?.scannedCode) {
      setFormData({
        ...formData,
        code: location.state.scannedCode,
        qr_value: location.state.scannedCode,
      });
      setShowCreateDialog(true);
      toast.info(`Code gescannt: ${location.state.scannedCode}`);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchAutomaten = async () => {
    setLoading(true);
    try {
      // Build query parameters - nutze serverseitiges Filtering
      const params = {
        limit: 500 // Erhöhe Limit für Gesamtansicht
      };
      if (defectOnlyFilter) {
        params.defectOnly = true;
      }
      
      const response = await axios.get(`${API}/automaten`, { params });
      setAutomaten(response.data || []);
      
      // PERFORMANCE-FIX: Hole Financials nur für aktuell sichtbare Automaten
      // und nur wenn nötig (Admin-Ansicht)
      // TODO: Batch-Endpunkt für Financials implementieren
      if (user?.role === 'admin' && response.data.length <= 50) {
        // Nur für kleine Datenmengen einzelne Calls machen
        const financialsData = {};
        await Promise.all(
          response.data.slice(0, 50).map(async (automat) => {
            try {
              const finRes = await axios.get(`${API}/automaten/${automat.id}/financials`);
              financialsData[automat.id] = finRes.data;
            } catch (err) {
              financialsData[automat.id] = { has_data: false };
            }
          })
        );
        setFinancials(financialsData);
      } else {
        // Bei größeren Datenmengen: Financials on-demand laden (bei Klick auf Automat)
        setFinancials({});
      }
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Automaten');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Automaten');
      }
      // Bei anderen Fehlern: leere Liste anzeigen ohne Toast
      setAutomaten([]);
      console.error('Automaten laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterAutomaten = () => {
    // Starte mit sortierten Automaten
    let filtered = sortedAutomaten;
    
    // NEU: Textsuche nach Automatennummer
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((a) => 
        (a.code || '').toLowerCase().includes(query)
      );
    }
    
    // Hide "AUSZÄHLEN" and "AKTIV" status for helpers
    if (user?.role === 'helper') {
      filtered = filtered.filter((a) => a.status !== 'AUSZÄHLEN' && a.status !== 'AKTIV');
    }
    
    // Apply status filter
    if (statusFilter === 'all') {
      setFilteredAutomaten(filtered);
    } else if (statusFilter === 'defekt') {
      // For defekt filter, check is_defect flag instead of status
      setFilteredAutomaten(filtered.filter((a) => a.is_defect === true));
    } else {
      setFilteredAutomaten(filtered.filter((a) => a.status === statusFilter));
    }
  };

  // NEU: Handler für Seiten-Scanner - VERWENDET ZENTRALEN RESOLVER
  const handlePageScan = async (qrValue) => {
    setShowPageScanner(false);
    
    // Normalisiere den Code
    const normalizedCode = qrValue.trim();
    
    try {
      // Nutze zentralen Scan-Resolver
      const result = await resolveScan({
        qrValue: normalizedCode,
        api: API,
        user,
        context: ScanContext.AUTOMAT_SUCHE
      });
      
      console.log(`[AutomatenPage] Scan-Ergebnis:`, result);
      
      if (result.type === ScanResultType.AUTOMAT) {
        toast.success(result.message);
        navigate(`/automaten/${result.data.id}`);
      } else if (result.type === ScanResultType.STANDORT) {
        toast.info(`Standort "${result.data.name}" gefunden - wechsle zu Standorte`);
        navigate(result.navigateTo);
      } else if (result.type === ScanResultType.UNKNOWN) {
        // EINHEITLICHER UNKNOWN-FLOW: Dialog anzeigen statt nur Toast
        console.log(`[AutomatenPage] Code nicht gefunden, zeige Erstellen-Dialog`);
        setScannedUnknownCode(normalizedCode);
        setShowUnknownCodeDialog(true);
      } else if (result.type === ScanResultType.ERROR) {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('[AutomatenPage] Scan-Fehler:', error);
      toast.error('Fehler beim Verarbeiten des Scans');
    }
  };

  // Handler für Unknown Code Dialog
  const handleCreateFromUnknown = (type) => {
    setShowUnknownCodeDialog(false);
    if (type === 'standort') {
      navigate('/standorte', { state: { openCreate: true, scannedCode: scannedUnknownCode } });
    } else {
      // Bei Automat: Dialog direkt öffnen mit vorausgefülltem Code
      setFormData({
        ...formData,
        code: scannedUnknownCode,
        qr_value: scannedUnknownCode,
      });
      setShowCreateDialog(true);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Sende Daten inkl. Defekt-Felder
      const createData = {
        ...formData,
        // Defektbeschreibung nur wenn defekt aktiv
        defect_note: formData.is_defect ? formData.defect_note : '',
      };
      await axios.post(`${API}/automaten`, createData);
      toast.success('Automat erstellt!');
      setShowCreateDialog(false);
      fetchAutomaten();
      setFormData({
        code: '',
        qr_value: '',
        machine_type: 'Standard',
        coin_type: '0,50',
        status: 'Lager LEER',
        is_defect: false,
        defect_note: '',
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Spezifische Fehlermeldungen für Duplikate
      if (detail === 'AUTOMAT_NUMBER_EXISTS') {
        toast.error('Automatennummer bereits vergeben. Bitte wählen Sie eine andere Nummer.');
      } else {
        toast.error(detail || 'Fehler beim Erstellen');
      }
    }
  };

  const handleDeleteAutomat = (automat) => {
    setAutomatToDelete(automat);
    setDeleteReason('');
    setShowDeleteDialog(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (!deleteReason || deleteReason.trim().length < 5) {
      toast.error('Löschgrund muss mindestens 5 Zeichen lang sein');
      return;
    }

    try {
      await axios.delete(`${API}/automaten/${automatToDelete.id}`, {
        params: { deleted_reason: deleteReason }
      });
      toast.success('Automat gelöscht!');
      setShowDeleteDialog(false);
      setAutomatToDelete(null);
      setDeleteReason('');
      fetchAutomaten();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleQRScan = (qrValue) => {
    setShowScanner(false);
    setFormData({ ...formData, code: qrValue, qr_value: qrValue });
    toast.success(`Code gescannt: ${qrValue}`);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-slide-in" data-testid="automaten-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Automaten
          </h1>
          <p className="text-sm sm:text-base text-slate-600">Verwalte deine Automaten</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Scan-Button LINKS */}
          <ScanButton
            onClick={() => setShowPageScanner(true)}
            variant="outline"
            data-testid="scan-automat-button"
          />
          {/* Neu-Button RECHTS */}
          {['admin', 'helper'].includes(user?.role) && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="flex items-center space-x-1 sm:space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600 px-2 sm:px-3 h-9 sm:h-10 text-sm" data-testid="create-automat-button">
                  <Plus size={18} className="sm:w-5 sm:h-5" />
                  <span className="hidden xs:inline">Neu</span>
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neuen Automaten erstellen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>Code (wird auch für QR-Code verwendet)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value, qr_value: e.target.value })}
                      required
                      placeholder="A-0001"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowScanner(true)}
                      data-testid="scan-code-button"
                    >
                      <QrCode size={20} className="mr-2" />
                      Scannen
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Manuell eingeben oder per Kamera scannen
                  </p>
                </div>
                <div>
                  <Label>Typ</Label>
                  <Select value={formData.machine_type} onValueChange={(value) => setFormData({ ...formData, machine_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Goliath">Goliath</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Münztyp</Label>
                  <Select value={formData.coin_type} onValueChange={(value) => setFormData({ ...formData, coin_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0,20">0,20 €</SelectItem>
                      <SelectItem value="0,50">0,50 €</SelectItem>
                      <SelectItem value="1,00">1,00 €</SelectItem>
                      <SelectItem value="2,00">2,00 €</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* NEU P1: Defekt-Checkbox beim Erstellen */}
                <div className="border-t pt-4 mt-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="create-defect" className="cursor-pointer">
                      Automat ist defekt
                    </Label>
                    <input
                      type="checkbox"
                      id="create-defect"
                      checked={formData.is_defect}
                      onChange={(e) => setFormData({ ...formData, is_defect: e.target.checked })}
                      className="h-5 w-5 rounded border-slate-300"
                    />
                  </div>
                  {formData.is_defect && (
                    <div className="mt-3">
                      <Label>Defektbeschreibung</Label>
                      <Textarea
                        value={formData.defect_note}
                        onChange={(e) => setFormData({ ...formData, defect_note: e.target.value })}
                        placeholder="Beschreibung des Defekts..."
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
                
                <Button type="submit" className="w-full" data-testid="submit-create-automat">
                  Erstellen
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* NEU: Suchfeld für Automatennummer */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Automatennummer suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                // Enter-Taste: Wenn genau 1 Treffer, navigiere zur Detailseite
                if (e.key === 'Enter' && filteredAutomaten.length === 1) {
                  navigate(`/automaten/${filteredAutomaten[0].id}`);
                }
              }}
              className="pl-10"
              data-testid="search-automat-input"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-slate-500" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="Lager LEER">Lager LEER</SelectItem>
                <SelectItem value="Lager VOLL">Lager VOLL</SelectItem>
                {user?.role === 'admin' && (
                  <>
                    <SelectItem value="AKTIV">AKTIV</SelectItem>
                    <SelectItem value="AUSZÄHLEN">AUSZÄHLEN</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          
          {/* Separate Defekt-Checkbox */}
          <div className="flex items-center space-x-2 border-l pl-4">
            <input
              type="checkbox"
              id="defect-filter"
              checked={defectOnlyFilter}
              onChange={(e) => setDefectOnlyFilter(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <label htmlFor="defect-filter" className="text-sm font-medium cursor-pointer">
              Nur defekte
            </label>
          </div>
          
          {/* Anzahl anzeigen */}
          <div className="text-sm text-slate-500 border-l pl-4">
            {filteredAutomaten.length} {filteredAutomaten.length === 1 ? 'Automat' : 'Automaten'}
          </div>
        </div>
      </Card>

      {filteredAutomaten.length === 0 ? (
        <Card className="p-12">
          <div className="text-center text-slate-500">
            <Box size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg">Keine Automaten gefunden</p>
            {searchQuery && (
              <p className="text-sm mt-2">
                Suche nach &quot;{searchQuery}&quot; ergab keine Treffer.{' '}
                <button 
                  onClick={() => setSearchQuery('')} 
                  className="text-blue-500 hover:underline"
                >
                  Suche zurücksetzen
                </button>
              </p>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filteredAutomaten.map((automat) => {
            const hasFinancials = financials[automat.id]?.has_data;
            
            return (
              <Card key={automat.id} className="p-3 sm:p-4 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-2 sm:mb-3">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <Box className="text-blue-600 flex-shrink-0" size={20} />
                    <div className="min-w-0">
                      <Link to={`/automaten/${automat.id}`}>
                        <h3 className="font-bold text-base sm:text-lg hover:text-blue-600 transition-colors truncate">
                          {automat.code}
                        </h3>
                      </Link>
                      <p className="text-xs sm:text-sm text-slate-500 truncate">{automat.machine_type}</p>
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAutomat(automat)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0 p-1.5 sm:p-2"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status:</span>
                    <span className={`font-medium ${
                      automat.status === 'AKTIV' ? 'text-green-600' :
                      automat.status === 'Lager VOLL' ? 'text-blue-600' :
                      automat.status === 'Lager LEER' ? 'text-orange-600' :
                      automat.status === 'AUSZÄHLEN' ? 'text-purple-600' :
                      'text-red-600'
                    }`}>
                      {automat.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Münztyp:</span>
                    <span className="font-medium">{automat.coin_type} €</span>
                  </div>
                  
                  {/* Teil A: Inhalt - KEINE Schacht-Labels, nur Produktname oder KEIN INHALT */}
                  <div className="pt-2 border-t mt-2 min-h-[24px] flex items-center">
                    {hasContent(automat) ? (
                      <span className="text-sm text-slate-700 font-medium truncate">
                        {/* Zeige nur den ersten Produktnamen, keine Schächte */}
                        {(() => {
                          const slots = getAllSlotsFill(automat, 1);
                          const firstFilled = slots.find(s => !s.isEmpty);
                          return firstFilled ? firstFilled.text : 'KEIN INHALT';
                        })()}
                      </span>
                    ) : (
                      <span className="text-sm text-red-500 font-semibold">KEIN INHALT</span>
                    )}
                  </div>
                  
                  {hasFinancials && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Erwarteter Umsatz:</span>
                        <span className="font-semibold text-green-600">
                          {financials[automat.id].expected_revenue.toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Erwarteter Gewinn:</span>
                        <span className="font-semibold text-blue-600">
                          {financials[automat.id].expected_profit.toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Marge:</span>
                        <span className="font-semibold text-purple-600">
                          {financials[automat.id].margin_percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* QR Scanner Modal für Code-Eingabe im Dialog */}
      {showScanner && (
        <Dialog open={showScanner} onOpenChange={setShowScanner}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>QR-Code scannen</DialogTitle>
            </DialogHeader>
            <QRScanner onScan={handleQRScan} />
          </DialogContent>
        </Dialog>
      )}

      {/* NEU: QR Scanner Modal für Seiten-Suche */}
      {showPageScanner && (
        <Dialog open={showPageScanner} onOpenChange={setShowPageScanner}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Automat scannen</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 mb-4">
              Scanne den QR-Code eines Automaten, um direkt zur Detailseite zu gelangen.
            </p>
            <QRScanner onScan={handlePageScan} />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Automat löschen</DialogTitle>
            </DialogHeader>
            <form onSubmit={confirmDelete} className="space-y-4">
              <p className="text-sm text-slate-600">
                Bist du sicher, dass du den Automaten <strong>{automatToDelete?.code}</strong> löschen möchtest?
              </p>
              <div>
                <Label>Löschgrund (mindestens 5 Zeichen)</Label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  required
                  minLength={5}
                  rows={3}
                  placeholder="Grund für das Löschen..."
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  className="flex-1"
                >
                  Löschen
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Unknown Code Dialog - Einheitlicher Dialog für unbekannte QR-Codes */}
      <UnknownCodeDialog
        open={showUnknownCodeDialog}
        onOpenChange={setShowUnknownCodeDialog}
        scannedCode={scannedUnknownCode}
        onCreateStandort={() => handleCreateFromUnknown('standort')}
        onCreateAutomat={() => handleCreateFromUnknown('automat')}
        showStandortOption={user?.role === 'admin'}
      />
    </div>
  );
};

export default AutomatenPage;